// (C) Copyright 2020 CNCC Services <https://www.cncc.fr/>
// This program is the sole property of CNCC Services and is not free software.

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Col, Form, message, Row, Select } from "antd";
import FormField from "../../../components/FormField";
import {
  AuthentifiedUser,
  NewsThemeInfoResponseDTO
} from "../../../../api/backend-openapi";
import { FIELD_LENGTH } from "../../../../utils/Constant";
import { useStore } from "effector-react";
import { NewsThemeStore } from "../../../../state/newsTheme/NewsThemeStore";
import { requestGetNewsThemes } from "../../../../state/newsTheme/NewsThemeEffects";
import { setNewsThemes } from "../../../../state/newsTheme/NewsThemeEvents";
import { UserFormSectionProps } from "../../../types/UserManagementTypes";
import { ProfilesStore } from "../../../../state/profiles/ProfilesStore";
import UserProfileTag from "./UserProfileTag";
import { BranchesStore } from "../../../../state/branches/BranchesStore";
import { authentifiedUserStore } from "../../../../state/auth/AuthStore";

type UserSettingsFormSectionProps = UserFormSectionProps & {
  resetNewsTheme: () => void;
  branch: string | undefined;
  setBranch: React.Dispatch<React.SetStateAction<string | undefined>>;
};

const UserSettingsFormSection: React.FunctionComponent<UserSettingsFormSectionProps> = (
  props: UserSettingsFormSectionProps
) => {
  const { t } = useTranslation();

  const {
    user,
    editMode,
    showRequired,
    resetNewsTheme,
    branch,
    setBranch
  } = props;

  const authentifiedUser: AuthentifiedUser | null = useStore(
    authentifiedUserStore
  );

  const newsThemeStore: NewsThemeInfoResponseDTO[] | null = useStore(
    NewsThemeStore
  );

  const profileList = useStore(ProfilesStore);

  const branchList = useStore(BranchesStore);

  useEffect(() => {
    requestGetNewsThemes();
  }, []);

  useEffect(() => {
    return requestGetNewsThemes.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setNewsThemes(result.data);
      } else {
        message.error(t("news_themes.load_failed"));
      }
    });
  });

  return (
    <>
      <Row>
        {user && (
          <Col xs={24} sm={8}>
            <FormField
              textArea={false}
              labelName={t("user.sub")}
              inputId="sub"
              required={showRequired}
              emptyErrorMessage={t("global.empty_error_field")}
              readOnly={true}
              maxLength={FIELD_LENGTH.ENTITY_ID}
            />
          </Col>
        )}
        <Col xs={24} sm={user ? 16 : 24}>
          <FormField
            textArea={false}
            labelName={t("user.email")}
            inputId="email"
            required={showRequired && editMode && !user}
            readOnly={
              editMode !== true || (user !== null && user !== undefined)
            }
            maxLength={FIELD_LENGTH.MAIL}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={24} sm={12}>
          <Form.Item
            name="branch"
            key="branch"
            required={
              showRequired &&
              authentifiedUser?.branch !== undefined &&
              authentifiedUser?.branch !== null
            }
            rules={[
              {
                required:
                  authentifiedUser?.branch !== undefined &&
                  authentifiedUser?.branch !== null,
                message: t("global.empty_error_field")
              }
            ]}
            label={<span>{t("add_users.branch.select")}</span>}
          >
            <Select
              id="branch"
              placeholder={t("add_users.branch.placeholder")}
              disabled={!editMode}
              allowClear={true}
              onChange={(e: string) => {
                setBranch(e);
                resetNewsTheme();
              }}
            >
              {Object.keys(branchList).map(key => (
                <Select.Option key={key} value={key}>
                  {branchList[key].label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="newsThemeIds"
            key="newsThemeIds"
            required={
              showRequired && (authentifiedUser?.newsThemeIds || []).length > 0
            }
            rules={[
              {
                required: (authentifiedUser?.newsThemeIds || []).length > 0,
                message: t("global.empty_error_field")
              }
            ]}
            label={<span>{t("add_users.theme.select")}</span>}
          >
            <Select
              id="newsThemeIds"
              placeholder={
                !!branch
                  ? t("add_users.theme.placeholder")
                  : t("add_users.theme.placeholderNoBranch")
              }
              mode="multiple"
              allowClear={true}
              disabled={!editMode}
            >
              {newsThemeStore
                .filter(
                  (newsTheme: NewsThemeInfoResponseDTO) =>
                    newsTheme.branch === branch
                )
                .map((newsTheme: NewsThemeInfoResponseDTO) => (
                  <Select.Option key={newsTheme.id} value={newsTheme.id}>
                    {newsTheme.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col xs={24}>
          <Form.Item
            name="profiles"
            key="profiles"
            label={<span>{t("add_users.rights.select")}</span>}
            rules={
              showRequired
                ? [
                    {
                      required: true,
                      message: t("global.empty_error_field")
                    }
                  ]
                : []
            }
          >
            <Select
              id="profiles"
              placeholder={t("add_users.rights.placeholder")}
              disabled={!editMode}
              mode="multiple"
            >
              {Object.keys(profileList).map(key => (
                <Select.Option key={key} value={key}>
                  <UserProfileTag profile={key}></UserProfileTag>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      {user && (
        <>
          <Row>
            <Col xs={24} sm={12}>
              <FormField
                labelName={t("user.id")}
                inputId="id"
                readOnly={true}
              />
            </Col>
            <Col xs={24} sm={12}>
              <FormField
                labelName={t("user.creation_date")}
                inputId="creationDate"
                readOnly={true}
              />
            </Col>
            <Col xs={24} sm={12}>
              <FormField
                labelName={t("user.modification_date")}
                inputId="modificationDate"
                readOnly={true}
              />
            </Col>
            <Col xs={24} sm={12}>
              <FormField
                labelName={t("user.last_connection_date")}
                inputId="lastConnectionDate"
                readOnly={true}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={24} sm={16} lg={12}>
              <FormField
                labelName={t("user.creator")}
                inputId="creator"
                readOnly={true}
              />
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

export default UserSettingsFormSection;
